import { graphql } from 'react-apollo'

import { compose, fromRenderProps, withProps } from 'recompose'

import withPartnerProps from 'components/HOCs/WithPartnerProps'
import { ActionType } from 'generated/types'
import { getAvailableFundingSources } from 'pages/Institutions/utils'
import trackEvent from 'utils/analytics/trackEvent'
import { withEnv } from 'utils/environment'
import { sendToSentry } from 'utils/errors'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import { PageToRoute } from 'utils/FunnelContext/types'
import { withOptimizely } from 'utils/Optimizely'

import fundingSourceInfoQuery from './fundingSourceInfo.query.gql'
import linkedAccountsQuery from './LinkedAccounts.query.gql'
import setPrimaryFundingSourceMutation from './SetPrimaryFundingSource.mutation.gql'

export type WithSetPrimaryFundingSourceProps = {
  setPrimaryFundingSource: (linkedSubaccountId: string) => Promise<any>
}

export const withSetPrimaryFundingSource = graphql(setPrimaryFundingSourceMutation, {
  props: ({ ownProps, mutate }: any) => ({
    setPrimaryFundingSource: (linkedSubaccountId: string) => {
      // Disabling ESLint for this line for no reason other than the code existed like this before this ESLint rule went into effect.
      // If you're here from the future, please resolve this ESLint error!
      // eslint-disable-next-line no-prototype-builtins
      if (ownProps.hasOwnProperty('setSubmitting')) {
        ownProps.setSubmitting(true)
      }
      return mutate({
        variables: { id: linkedSubaccountId },
      }).catch(error => {
        sendToSentry(error, { breadcrumb: 'User is attempting to set primary funding source.' })
      })
    },
  }),
})

enum BankLinkingAction {
  PartnerSkip,
  AlreadyLinked,
  ChooseFundingSource,
  SetFundingSource,
  LinkAccount,
}

const getFundingSources = linkedAccounts => {
  let allSubaccounts = []
  let availableFundingSources = []
  linkedAccounts.forEach(async linkedAccount => {
    allSubaccounts = allSubaccounts.concat(linkedAccount.linkedSubaccounts)
    availableFundingSources = availableFundingSources.concat(
      getAvailableFundingSources(linkedAccount.linkedSubaccounts),
    )
  })
  return { allSubaccounts, availableFundingSources }
}

const determineAction = (fundingSourceInfo, linkedAccounts, partner, resumeEnabled) => {
  if (fundingSourceInfo && partner && partner.user) {
    return BankLinkingAction.PartnerSkip
  }

  if (!resumeEnabled) {
    return BankLinkingAction.LinkAccount
  }

  if (fundingSourceInfo) {
    return BankLinkingAction.AlreadyLinked
  }

  if (!fundingSourceInfo && linkedAccounts && linkedAccounts.length) {
    const { availableFundingSources } = getFundingSources(linkedAccounts)

    if (availableFundingSources.length > 1) {
      return BankLinkingAction.ChooseFundingSource
    } else if (availableFundingSources.length === 1) {
      return BankLinkingAction.SetFundingSource
    }
  }
}

export const withSkipBankLinking = compose(
  fromRenderProps<Partial<FunnelContextProps>, {}, Partial<FunnelContextProps>>(
    FunnelContext.Consumer,
    ({ isDynamicFunnelEnabled, routeToPage }) => ({ isDynamicFunnelEnabled, routeToPage }),
  ),
  withEnv,
  withSetPrimaryFundingSource,
  withPartnerProps,
  withOptimizely,
  graphql(linkedAccountsQuery, { options: { fetchPolicy: 'network-only' } }),
  withProps(
    ({
      flow,
      route,
      partner,
      data: { loading, fundingSourceInfo, linkedAccounts },
      setPrimaryFundingSource,
      trackOptimizely,
      env,
      isDynamicFunnelEnabled,
      routeToPage,
    }) => {
      if (loading) {
        return { loading }
      }

      const resumeEnabled = env.get('resumeEnabled') === 'true'
      const result = determineAction(fundingSourceInfo, linkedAccounts, partner, resumeEnabled)
      switch (result) {
        case BankLinkingAction.PartnerSkip:
          {
            route({
              action: 'partner_skip',
              state: { partner },
              doNotTrackVisited: true,
            })
          }
          break
        case BankLinkingAction.AlreadyLinked: {
          const state = {
            financialInstitution: fundingSourceInfo,
            linkedSubaccounts: linkedAccounts,
          }
          const page = Object.keys(PageToRoute).find(key => PageToRoute[key] === location.pathname)
          isDynamicFunnelEnabled
            ? routeToPage(page, ActionType.Continue, state)
            : route({ action: 'skip', doNotTrackVisited: false, locationState: state })
          break
        }
        case BankLinkingAction.ChooseFundingSource:
          {
            const { allSubaccounts } = getFundingSources(linkedAccounts)
            route({
              action: 'choose',
              locationState: { linkedSubaccounts: allSubaccounts },
            })
          }
          break
        case BankLinkingAction.SetFundingSource: {
          const { availableFundingSources } = getFundingSources(linkedAccounts)
          const { id: linkedSubaccountId } = availableFundingSources[0]

          setPrimaryFundingSource(linkedSubaccountId, fundingSourceInfoQuery).then(() => {
            trackOptimizely('funding_source.linked')
            trackEvent('PlaidLink:Success', { location, partner, flow })
            route({ from: FlowRoute.Funding, action: 'skip', doNotTrackVisited: true })
          })

          return { loading: true }
        }
        default: {
          return {
            fundingSourceInfo,
            loading,
          }
        }
      }
    },
  ),
)
